import usePostItem from "@Hooks/usePostItem";
import { setSnackbar } from "@Redux/snackBarSlice";
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Tabs,
  Tab,
  Paper,
  Chip,
  FormControlLabel,
  Switch,
  Tooltip,
} from "@mui/material";
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import CategoryList from "./CategoriesBox/CategoryList";
import RegionsSideBar from "./RegionsSideBar";
import { NewIngredientContainer } from "./CreateIngredientExitStyles";
import PageTitle from "@Components/PageTitle/PageTitle";
import themeCreator from "@Themes/themeCreator";
import { TabContext, TabPanel } from "@mui/lab";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import EditIcon from "@mui/icons-material/Edit";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DatePicker from "@Components/DateTimePickers/DatePicker";
import LoadingComponent from "@Components/LoadingComponent/LoadingComponent";
import dayjs from "dayjs";
import SearchIcon from "@mui/icons-material/Search";
import useGetItem from "@Hooks/useGetItem";
import useGetAll from "@Hooks/useGetAll";
import * as DefaultRoutes from "@Routes/DefaultRoutes";
import {
  ADD_EXIT_REQUEST,
  GET_USER_BY_ROLE,
  UPDATE_EXIT_REQUEST,
  SAVE_NEW_INGREDIENTS,
  EXIT_REQUESTS,
} from "@API/APIRoutes";
import { red } from "@mui/material/colors";
import CancelIcon from "@mui/icons-material/Cancel";
import useSendGetRequest from "@Hooks/useSendGetRequest";
import { useNavigationGuard } from "@Hooks/useNavigationGuard";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const CreateIngredientExit = () => {
  // -------------------- ATTRIBUTES --------------------
  const theme = themeCreator();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const MANAGER_NAME = "APPROVER";

  // Check for edit mode from different routes
  // 1. From the new dedicated edit route
  const requestIdFromPath = params.reqId;
  // 2. From the old query parameter approach (fallback)
  const queryParams = new URLSearchParams(location.search);
  const editIdFromQuery = queryParams.get("edit");

  // Combine both approaches - prioritize the path parameter
  const editId = requestIdFromPath || editIdFromQuery;
  const isEditMode = !!editId;

  // Check for clone mode - data passed via navigation state
  const navigationState = location.state || {};
  const { cloneData, clonedFrom } = navigationState;
  const isCloneMode = !!cloneData;

  // -------------------- STATES --------------------
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [regions, setRegions] = useState([]);
  const [targetedMem, settargetedMem] = useState();

  const [currentRegion, setCurrentRegion] = useState("");
  const [selectedCountries, setSelectedCountries] = useState({});
  const [countryError, setCountryError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [primaryDate, setPrimaryDate] = useState(null);
  const [primaryDateError, setPrimaryDateError] = useState("");
  const [primaryDateType, setPrimaryDateType] = useState("");
  const [primaryDateTypeError, setPrimaryDateTypeError] = useState("");
  const [secondaryDate, setSecondaryDate] = useState(null);
  const [secondaryDateType, setSecondaryDateType] = useState("");
  const [ulInternalDate, setUlInternalDate] = useState(null);

  // API Data states
  const [ingredients, setIngredients] = useState([]);
  const [ingredientsLoading, setIngredientsLoading] = useState(true);

  const [selectedIngredient, setSelectedIngredient] = useState(null);
  const [ingredientError, setIngredientError] = useState("");
  const [approvalmanagersError, setApprovalmanagersError] = useState("");
  const [isCustomIngredient, setIsCustomIngredient] = useState(false);
  const [customIngredientName, setCustomIngredientName] = useState("");
  const [customIngredientDescription, setCustomIngredientDescription] =
    useState("");

  const [customCasNumber, setCustomCasNumber] = useState("");
  const [customIngredientErrors, setCustomIngredientErrors] = useState({
    name: "",
    casNumber: "",
  });

  const [restrictionType, setRestrictionType] = useState("");
  const [restrictionTypeError, setRestrictionTypeError] = useState("");
  const [additionalComments, setAdditionalComments] = useState("");
  const [selectedManagerId, setSelectedManagerId] = useState("");
  const requiresCategoryDescriptions =
    restrictionType && restrictionType !== "BANNED";

  // -------------------- UNSAVED CHANGES TRACKING --------------------
  const formLoaded = useRef(false);
  const [initialFormState, setInitialFormState] = useState(null);

  const hasUnsavedChanges = useMemo(() => {
    if (!formLoaded.current) return false;
    if (!initialFormState) {
      // Create mode: any field filled = unsaved changes
      return !!(
        selectedIngredient ||
        isCustomIngredient ||
        restrictionType ||
        primaryDate ||
        selectedCategories.length > 0 ||
        Object.values(selectedCountries).some((arr) => arr && arr.length > 0) ||
        additionalComments ||
        selectedManagerId
      );
    }
    // Edit mode: compare against loaded state
    return (
      JSON.stringify(selectedIngredient) !== JSON.stringify(initialFormState.selectedIngredient) ||
      restrictionType !== initialFormState.restrictionType ||
      (primaryDate?.toString() || '') !== (initialFormState.primaryDate?.toString() || '') ||
      JSON.stringify(selectedCategories) !== JSON.stringify(initialFormState.selectedCategories) ||
      JSON.stringify(selectedCountries) !== JSON.stringify(initialFormState.selectedCountries) ||
      additionalComments !== initialFormState.additionalComments ||
      selectedManagerId !== initialFormState.selectedManagerId
    );
  }, [selectedIngredient, isCustomIngredient, restrictionType, primaryDate, selectedCategories, selectedCountries, additionalComments, selectedManagerId, initialFormState]);

  useNavigationGuard(hasUnsavedChanges, 'You have unsaved changes. Are you sure you want to leave this page?');

  // -------------------- API HOOKS --------------------
  const dispatch = useDispatch();
  const { loading: postLoading, postItem } = usePostItem();
  const getItem = useGetItem();
  const { getAll: getAllIngredients } = useGetAll();

  const [approvalManagersList, setApprovalManagersList] = useState([]);
  const { loading, getData: getData } = useSendGetRequest();

  const [approvalManagersOptions, setapprovalManagersOptions] = useState([]);

  // ---- UI Exit Request state ----
  const [isUiExitRequest, setIsUiExitRequest] = useState(false);
  const [selectedCoordinatorId, setSelectedCoordinatorId] = useState("");
  const [coordinatorError, setCoordinatorError] = useState("");
  const [coordinatorOptions, setCoordinatorOptions] = useState([]);
  const [selectedCoordinatorDisplay, setSelectedCoordinatorDisplay] = useState(null);

  // Mark form as loaded and capture initial state for unsaved changes tracking
  useEffect(() => {
    if (!isLoading && !ingredientsLoading && !loading) {
      if (!formLoaded.current) {
        formLoaded.current = true;
        if (isEditMode) {
          setInitialFormState({
            selectedIngredient,
            restrictionType,
            primaryDate: primaryDate?.toString() || '',
            selectedCategories: JSON.parse(JSON.stringify(selectedCategories)),
            selectedCountries: JSON.parse(JSON.stringify(selectedCountries)),
            additionalComments,
            selectedManagerId,
          });
        }
      }
    }
  }, [isLoading, ingredientsLoading, loading]);

  // -------------------- METHODS --------------------

  useEffect(() => {
    setIngredientsLoading(true);
    getAllIngredients("/ingredient/all", "Failed to fetch ingredients")
      .then((data) => {
        if (data) {
          setIngredients(data);
        }
      })
      .finally(() => {
        setIngredientsLoading(false);
      });
  }, []);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow || "";
    };
  }, []);

  useEffect(() => {
    const promises = [getData(GET_USER_BY_ROLE, { roleName: MANAGER_NAME })];
    Promise.all(promises)
      .then(([managers]) => {
        console.log(managers);
        const updatedManagers = managers.map((manager) => ({
          id: manager.id,
          name: manager.email,
        }));
        setapprovalManagersOptions(updatedManagers);
        console.log(updatedManagers);
      })
      .catch((error) => { });
  }, []);

  // Fetch R&D Coordinator users
  useEffect(() => {
    getData(GET_USER_BY_ROLE, { roleName: "RD_COORDINATOR" })
      .then((coordinators) => {
        if (coordinators) {
          setCoordinatorOptions(coordinators.map((c) => ({ id: c.id, name: c.email })));
        }
      })
      .catch(() => {});
  }, []);

  // Handle clone mode - pre-fill form with cloned data
  useEffect(() => {
    if (isCloneMode && ingredients.length > 0) {
      setIsLoading(true);

      try {
        // Find and set the ingredient from the CAS number
        const foundIngredient = ingredients.find(
          (ing) => ing.casNumber === cloneData.casNumber
        );

        if (foundIngredient) {
          setSelectedIngredient(foundIngredient);
        } else {
          // Ingredient not found in list - would need to handle as custom ingredient
          console.warn("Copied ingredient not found in current ingredient list:", cloneData.casNumber);
        }

        // Set date fields
        if (cloneData.primaryDate) {
          setPrimaryDate(dayjs(cloneData.primaryDate));
        }
        if (cloneData.primaryDateType) {
          setPrimaryDateType(cloneData.primaryDateType);
        }
        if (cloneData.secondaryDate) {
          setSecondaryDate(dayjs(cloneData.secondaryDate));
          setSecondaryDateType(cloneData.secondaryDateType || "");
        }
        if (cloneData.ulInternalDate) {
          setUlInternalDate(dayjs(cloneData.ulInternalDate));
        }

        // Set restriction type
        if (cloneData.restrictionType) {
          setRestrictionType(cloneData.restrictionType);
        }

        // Set additional comments
        if (cloneData.additionalInfo) {
          setAdditionalComments(cloneData.additionalInfo);
        }

        // Note: We explicitly do NOT set:
        // - selectedCategories (RD assignments)
        // - selectedCountries (country/region selections)
        // - approvalManagersList
        // These need to be re-selected by the user for the new cloned request

        dispatch(
          setSnackbar({
            open: true,
            message: "Exit request copied successfully. Please re-assign R&D members, countries, and approvers.",
            error: false,
          })
        );
      } catch (error) {
        console.error("Error processing copied data:", error);
        dispatch(
          setSnackbar({
            open: true,
            message: "Failed to process copied exit request data",
            error: true,
          })
        );
      } finally {
        setIsLoading(false);
      }
    }
  }, [isCloneMode, cloneData, ingredients]);

  // Fetch request data if in edit mode
  useEffect(() => {
    if (isEditMode && ingredients.length > 0) {
      setIsLoading(true);

      getItem(EXIT_REQUESTS, editId)
        .then((data) => {
          if (!data) {
            dispatch(
              setSnackbar({
                open: true,
                message: "Failed to fetch exit request data",
                error: true,
              })
            );
            return;
          }

          // ===== DEBUG: Log the full API response =====
          console.group("[EditMode] API Response");
          console.log("Full data:", JSON.stringify(data, null, 2));
          console.log("isUiExitRequest:", data.isUiExitRequest, "(type:", typeof data.isUiExitRequest, ")");
          console.log("targetedMembers:", data.targetedMembers);
          console.groupEnd();
          // ============================================

          setSelectedIngredient(data.ingredient);

          // Restore UI exit request flag so the toggle and coordinator section render correctly
          const uiRequest = data.isUiExitRequest === true;
          console.log("[EditMode] uiRequest:", uiRequest);
          setIsUiExitRequest(uiRequest);

          // Set date fields
          setPrimaryDate(dayjs(data.primaryDate));
          setPrimaryDateType(data.primaryDateType || "");
          if (data.secondaryDate) {
            setSecondaryDate(dayjs(data.secondaryDate));
            setSecondaryDateType(data.secondaryDateType || "");
          }
          if (data.ulInternalDate) {
            setUlInternalDate(dayjs(data.ulInternalDate));
          }
          setRestrictionType(data.restrictionType || "");
          setAdditionalComments(data.additionalInfo || "");

          if (data.targetedMembers && data.targetedMembers.length) {
            console.log("[EditMode] Setting targetedMem:", data.targetedMembers);
            settargetedMem(data.targetedMembers);

            // For UI exit requests, find and restore the R&D Coordinator from targetedMembers.
            // The coordinator is identified by the RD_COORDINATOR role on their user record.
            if (uiRequest) {
              const coordinatorMember = data.targetedMembers.find(
                (m) => m.role === "RD_COORDINATOR"
              );
              console.log("[EditMode] coordinatorMember found:", coordinatorMember);
              if (coordinatorMember) {
                setSelectedCoordinatorId(coordinatorMember.id);
                setSelectedCoordinatorDisplay({
                  id: coordinatorMember.id,
                  name: coordinatorMember.email || coordinatorMember.username,
                });
              }
            }
          } else {
            console.warn("[EditMode] No targetedMembers in response!");
          }

          const approvalManagersList = data.approvingManagers.map(
            (manager) => ({
              id: manager.id,
              name: manager.email,
            })
          );
          setapprovalManagersOptions((prevOptions) =>
            prevOptions.filter(
              (option) =>
                !approvalManagersList.some(
                  (manager) => manager.id === option.id
                )
            )
          );
          setApprovalManagersList(approvalManagersList);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else if (!isEditMode) {
      setIsLoading(false);
    }
  }, [editId, isEditMode, ingredients]);

  const handlePrimaryDateChange = (date) => {
    // Check if date is before current date
    if (date && dayjs(date).isBefore(dayjs(), "day")) {
      setPrimaryDateError("Date cannot be prior to the current date");
      return;
    }

    setPrimaryDate(date);
    setPrimaryDateError("");
  };

  const handleSecondaryDateChange = (date) => {
    // Secondary date is optional and can be any date
    setSecondaryDate(date);
  };

  const handleIngredientTabChange = (_, newValue) => {
    const custom = newValue === 1;
    setIsCustomIngredient(custom);

    if (custom) {
      // switching to custom ingredient mode, clear any selection from list
      setSelectedIngredient(null);
      setIngredientError("");
    } else {
      // back to list mode – reset custom ingredient fields/errors
      setCustomIngredientName("");
      setCustomCasNumber("");
      setCustomIngredientDescription("");
      setCustomIngredientErrors({
        name: "",
        casNumber: "",
        description: "",
      });
      setIngredientError("");
    }
  };

  // Validation function that checks all form fields
  const validateAllFields = () => {
    let isValid = true;

    // ---- Validate ingredient selection ----
    if (isCustomIngredient) {
      const ingredientErrors = {
        name: "",
        casNumber: "",
        description: "",
      };

      if (!customIngredientName.trim()) {
        ingredientErrors.name = "Ingredient name is required";
        isValid = false;
      }

      if (!customCasNumber.trim()) {
        ingredientErrors.casNumber = "CAS number is required";
        isValid = false;
      } else {
        // Check for duplicate CAS number (with trimmed comparison)
        const trimmedCasNumber = customCasNumber.trim();
        const existingIngredient = ingredients.find(
          (ing) => ing.casNumber && ing.casNumber.trim() === trimmedCasNumber
        );
        if (existingIngredient) {
          ingredientErrors.casNumber = `An ingredient with CAS number "${trimmedCasNumber}" already exists: ${existingIngredient.name}`;
          isValid = false;
        }
      }

      if (!customIngredientDescription.trim()) {
        ingredientErrors.description = "Ingredient Description is required";
        isValid = false;
      }

      setCustomIngredientErrors(ingredientErrors);
    } else if (!selectedIngredient) {
      setIngredientError("Please select an ingredient");
      isValid = false;
    } else {
      setIngredientError("");
    }

    // ---- Validate primary date ----
    if (!primaryDate) {
      setPrimaryDateError("Primary date is required");
      isValid = false;
    } else if (dayjs(primaryDate).isBefore(dayjs(), "day")) {
      setPrimaryDateError("Date cannot be prior to the current date");
      isValid = false;
    } else {
      setPrimaryDateError("");
    }

    // ---- Validate primary date type ----
    if (!primaryDateType) {
      setPrimaryDateTypeError("Primary date type is required");
      isValid = false;
    } else {
      setPrimaryDateTypeError("");
    }

    // ---- Validate restriction type ----
    if (!restrictionType) {
      setRestrictionTypeError("Restriction type is required");
      isValid = false;
    } else {
      setRestrictionTypeError("");
    }

    // ---- Validate categories ----
    if (selectedCategories.length === 0) {
      dispatch(
        setSnackbar({
          open: true,
          message: "Exit request should have at least one category specified!",
          error: true,
        })
      );
      isValid = false;
    }

    // ---- validate if (approvalManagersList.length !== 0)

    if (approvalManagersList.length === 0) {
      setApprovalmanagersError(
        "At least one approval manager must be selected."
      );
      isValid = false;
    } else {
      setApprovalmanagersError("");
    }

    // ---- Validate R&D Coordinator (mandatory for UI exit requests) ----
    if (isUiExitRequest && !selectedCoordinatorId) {
      setCoordinatorError("An R&D Coordinator must be selected for UI Exit Requests.");
      isValid = false;
    } else {
      setCoordinatorError("");
    }

    // ---- Validate R&D members ----
    const categoriesWithoutMembers = selectedCategories.filter(
      (category) => !category.selectedMember
    );

    if (categoriesWithoutMembers.length > 0) {
      dispatch(
        setSnackbar({
          open: true,
          message: "Each category must have a selected R&D member!",
          error: true,
        })
      );
      isValid = false;
    }

    // Clear any leftover restriction description errors (description is optional)
    setSelectedCategories((prevCategories) =>
      prevCategories.map((category) =>
        category.restrictionDescriptionError
          ? { ...category, restrictionDescriptionError: false }
          : category
      )
    );

    return isValid;
  };

  const createNewIngrediantToAddExitRequestCallback = () => {
    const grouped = {};

    selectedCategories.forEach((category) => {
      const memberId = category.selectedMember;
      if (!memberId) {
        return;
      }
      const memberKey = memberId.toString();
      if (!grouped[memberKey]) {
        grouped[memberKey] = [];
      }
      const categoryId =
        typeof category.id === "number"
          ? category.id
          : parseInt(category.id, 10);
      if (Number.isNaN(categoryId)) {
        return;
      }
      grouped[memberKey].push({
        categoryId,
        description:
          category.restrictionDescription &&
            category.restrictionDescription.trim().length > 0
            ? category.restrictionDescription.trim()
            : null,
      });
    });

    const selectedMembers = Object.entries(grouped).map(
      ([memberId, categories]) => ({
        userId: parseInt(memberId, 10),
        categories,
      })
    );

    const approvalManagersId = approvalManagersList.map(
      (manager) => manager.id
    );

    // Flatten all selected countries from all regions into a single array of IDs
    // Backend expects: countryIds: Set<Long>
    const allCountryIds = Object.values(selectedCountries)
      .flat() // Flatten arrays from all regions
      .map((country) => country.id); // Extract IDs

    // Create request data
    const requestData = {
      casNumber: isCustomIngredient
        ? customCasNumber.trim()
        : selectedIngredient.casNumber,
      primaryDate: primaryDate.format("YYYY-MM-DD"),
      primaryDateType: primaryDateType,
      secondaryDate: secondaryDate ? secondaryDate.format("YYYY-MM-DD") : null,
      secondaryDateType: secondaryDate ? secondaryDateType : null,
      ulInternalDate: ulInternalDate ? ulInternalDate.format("YYYY-MM-DD") : null,
      restrictionType: restrictionType,
      additionalInfo: additionalComments,
      assignments: selectedMembers,
      approvingUsers: approvalManagersId,
      countryIds: allCountryIds, // Flat list of all country IDs
      isUiExitRequest: isUiExitRequest,
      // Build coordinatorAssignment: userId + deduplicated category IDs from all selected categories
      coordinatorAssignment: isUiExitRequest && selectedCoordinatorId
        ? {
            userId: parseInt(selectedCoordinatorId),
            categoryIds: [...new Set(selectedCategories.map((c) => {
              const catId = typeof c.id === "number" ? c.id : parseInt(c.id, 10);
              return catId;
            }).filter((id) => !Number.isNaN(id)))],
          }
        : null,
    };

    if (isEditMode) {
      requestData.id = parseInt(editId);
    }

    console.log("Sending request data to API:", requestData);
    console.log("Selected countries per region:", selectedCountries);

    postItem(
      isEditMode ? UPDATE_EXIT_REQUEST : ADD_EXIT_REQUEST,
      requestData,
      () => {
        navigate(DefaultRoutes.myActiveReqs);
      },
      isEditMode
        ? "Exit Request Updated Successfully!"
        : "Exit Request Created Successfully!",
      isEditMode
        ? "Failed to update Exit Request"
        : "Failed to create Exit Request"
    );
  };
  // Direct submission handler for the save button
  const handleSaveClick = () => {
    console.log("Save button clicked - running validation");
    // Validate all fields
    if (!validateAllFields()) {
      console.log("Validation failed");
      return;
    }

    if (isCustomIngredient) {
      const newIngrediant = {
        casNumber: customCasNumber.trim(),
        name: customIngredientName.trim(),
        description: customIngredientDescription.trim(),
      };
      postItem(
        SAVE_NEW_INGREDIENTS,
        newIngrediant,
        createNewIngrediantToAddExitRequestCallback
      );
    } else {
      createNewIngrediantToAddExitRequestCallback();
    }
  };

  const handleOnCancel = () => {
    // Always return to My Requests page since that's where edits come from now
    navigate(DefaultRoutes.myActiveReqs);
  };

  const handleDeleteItem = (category) => {
    if (category.name) {
      // Remove the category from selected categories
      setSelectedCategories(
        selectedCategories.filter((item) => item !== category)
      );

      // Only add the category back to the available categories if
      // we're currently viewing its original region
      if (currentRegion && category.tag.id === parseInt(currentRegion)) {
        setCategories((prev) => [...prev, category]);
      }
    }
  };

  // Updated to use both category ID and region ID for unique identification
  const handleSelectMember = (categoryId, regionId, selectedMember) => {
    console.log(
      `Selecting member ${selectedMember} for category ${categoryId} in region ${regionId}`
    );
    setSelectedCategories((prevCategories) =>
      prevCategories.map((category) =>
        // Match on both category ID and region ID to ensure uniqueness
        category.id === categoryId && category.tag.id === regionId
          ? { ...category, selectedMember: selectedMember }
          : category
      )
    );
  };

  const handleCategoryDescriptionChange = (categoryId, regionId, value) => {
    setSelectedCategories((prevCategories) =>
      prevCategories.map((category) => {
        if (
          category.id === categoryId &&
          category.tag.id === regionId
        ) {
          const trimmedValue = value ?? "";
          return {
            ...category,
            restrictionDescription: trimmedValue,
            restrictionDescriptionError:
              trimmedValue.trim().length > 0
                ? false
                : category.restrictionDescriptionError,
          };
        }
        return category;
      })
    );
  };

  // If still loading the data, show a loading indicator
  if (isLoading || ingredientsLoading || loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <LoadingComponent size="5em" thickness={5} color="primary" />
      </Box>
    );
  }

  // -------------------- HTML TO BE RENDERED --------------------
  return (
    <NewIngredientContainer className="newIngredientRoot">
      <Grid container rowSpacing={2} columnSpacing={{ xs: 0, md: 2 }}>
        <Grid item xs={12} md={8.5} className="formColumn">
          <Box className="formHeader">
            {/* UI Exit Request toggle — top-left of the header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 1,
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={isUiExitRequest}
                    onChange={(e) => {
                      setIsUiExitRequest(e.target.checked);
                      if (e.target.checked) {
                        // Switching to UI mode - clear region selection
                        setCurrentRegion("");
                      }
                      if (!e.target.checked) {
                        setSelectedCoordinatorId("");
                        setSelectedCoordinatorDisplay(null);
                        setCoordinatorError("");
                        // Switching back to normal mode - clear region selection
                        setCurrentRegion("");
                      }
                    }}
                    color="primary"
                    disabled={isEditMode}
                  />
                }
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: isUiExitRequest ? 700 : 400,
                        color: isUiExitRequest ? "primary.main" : "text.secondary",
                      }}
                    >
                      UI Exit Request
                    </Typography>
                    <Tooltip
                      title="When enabled, the R&D Coordinator will define the product list shared across all regions for each category, before R&D members start their analysis."
                      placement="right"
                    >
                      <InfoOutlinedIcon
                        sx={{ fontSize: 16, color: "text.disabled", cursor: "help" }}
                      />
                    </Tooltip>
                  </Box>
                }
                sx={{ m: 0 }}
              />
              {isUiExitRequest && (
                <Chip
                  label="UI"
                  size="small"
                  color="primary"
                  sx={{ fontWeight: 700, fontSize: "0.7rem", height: 22 }}
                />
              )}
            </Box>

            <PageTitle
              title={
                isEditMode ? "Edit Exit Request" :
                isCloneMode ? "Clone Exit Request" :
                "Ingredient Exit Request"
              }
              PageIcon={
                isEditMode ? (
                  <EditIcon className="icon" fontSize="large" />
                ) : isCloneMode ? (
                  <ContentCopyIcon className="icon" fontSize="large" />
                ) : (
                  <ExitToAppIcon className="icon" fontSize="large" />
                )
              }
              iconColor={theme.palette.primary.main}
            />
          </Box>
          <Box className="formBody">
            <Grid container spacing={2}>
              {/* Ingredient Selection Section */}
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  variant="outlined"
                  className="formSection"
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      mb: 0.5,
                      width: "100%",
                    }}
                  >
                    {/* Only show mode tabs in create mode, not edit mode */}
                    {!isEditMode && (
                      <Tabs
                        value={isCustomIngredient ? 1 : 0}
                        onChange={handleIngredientTabChange}
                        variant="fullWidth"
                        sx={{
                          width: "100%",
                          mt: 1,
                          borderBottom: `2px solid ${theme.palette.divider}`,
                          backgroundColor: theme.palette.grey[50],
                          "& .MuiTabs-indicator": {
                            backgroundColor: theme.palette.primary.main,
                            height: "4px",
                          },
                          "& .MuiTab-root": {
                            flex: 1,
                            fontWeight: 600,
                            fontSize: "0.875rem",
                            textTransform: "none",
                            transition: "all 0.3s ease",
                            color: theme.palette.text.secondary,
                            py: 1,
                            "&:hover": {
                              backgroundColor: theme.palette.grey[100],
                              color: theme.palette.primary.main,
                            },
                            "&.Mui-selected": {
                              color: theme.palette.primary.main,
                              fontWeight: 700,
                            },
                          },
                        }}
                      >
                        <Tab label="Select from List" />
                        <Tab label="Add Custom Ingredient" />
                      </Tabs>
                    )}
                  </Box>

                  {!isCustomIngredient ? (
                    // Ingredient Selection Dropdown
                    <Autocomplete
                      options={ingredients}
                      getOptionLabel={(option) =>
                        `${option.name} (${option.casNumber})`
                      }
                      value={selectedIngredient}
                      onChange={(_, newValue) => {
                        setSelectedIngredient(newValue);
                        if (newValue) {
                          setIngredientError("");
                        }
                      }}
                      disabled={isEditMode}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          fullWidth
                          placeholder="Search ingredient by name or CAS number"
                          required
                          error={!!ingredientError}
                          helperText={ingredientError}
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <SearchIcon
                                  sx={{ color: "action.active", mr: 1 }}
                                />
                                {params.InputProps.startAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  ) : (
                    // Custom Ingredient Form
                    <Grid container spacing={1.5}>
                      <Grid item xs={6}>
                        <TextField
                          label="Ingredient Name"
                          size="small"
                          fullWidth
                          value={customIngredientName}
                          onChange={(e) =>
                            setCustomIngredientName(e.target.value)
                          }
                          disabled={isEditMode}
                          error={!!customIngredientErrors.name}
                          helperText={customIngredientErrors.name}
                          placeholder="Enter ingredient name"
                          required
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          label="CAS Number"
                          size="small"
                          fullWidth
                          value={customCasNumber}
                          onChange={(e) => setCustomCasNumber(e.target.value)}
                          disabled={isEditMode}
                          error={!!customIngredientErrors.casNumber}
                          helperText={customIngredientErrors.casNumber}
                          placeholder="Enter CAS number "
                          required
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Ingredient Description"
                          size="small"
                          fullWidth
                          rows={2}
                          multiline
                          value={customIngredientDescription}
                          onChange={(e) =>
                            setCustomIngredientDescription(e.target.value)
                          }
                          disabled={isEditMode}
                          error={!!customIngredientErrors.description}
                          helperText={customIngredientErrors.description}
                          placeholder="Enter Ingredient Description"
                          required
                        />
                      </Grid>
                    </Grid>
                  )}
                </Paper>
              </Grid>

              {/* Dates & Restriction Section */}
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  variant="outlined"
                  className="formSection"
                >
                  <Typography className="sectionTitle">
                    Dates & Restriction Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ width: "100%" }}>
                        <DatePicker
                          name=""
                          value={primaryDate}
                          error={primaryDateError}
                          onPick={handlePrimaryDateChange}
                          views={["year", "month", "day"]}
                          minDate={dayjs()}
                          label="Primary Date *"
                          format="MM/DD/YYYY"
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ width: "100%", mt: 0.5 }}>
                        <FormControl
                          size="small"
                          fullWidth
                          error={!!primaryDateTypeError}
                        >
                          <InputLabel id="primary-date-type-label">
                            Primary Date Type *
                          </InputLabel>
                          <Select
                            labelId="primary-date-type-label"
                            value={primaryDateType}
                            label="Primary Date Type *"
                            onChange={(e) => {
                              setPrimaryDateType(e.target.value);
                              if (e.target.value) {
                                setPrimaryDateTypeError("");
                              }
                            }}
                            required
                            variant="outlined"
                          >
                            {[
                              { value: "OFF_SHELF_BAN_DATE", label: "Off-Shelf Ban Date" },
                              { value: "MA_DATE", label: "Market Available (MA) Date" },
                              { value: "POM_DATE", label: "Place On Market (POM) Date" },
                              { value: "LAST_PRODUCTION_DATE", label: "Last Production Date" },
                              { value: "LAST_IMPORTATION_DATE", label: "Last Importation Date" },
                              { value: "DISPOSAL_DATE", label: "Disposal Date" },
                              { value: "COMMENCEMENT_DATE", label: "Commencement Date" },
                            ]
                              .filter((opt) => opt.value !== secondaryDateType)
                              .map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                  {opt.label}
                                </MenuItem>
                              ))}
                          </Select>
                          {primaryDateTypeError && (
                            <FormHelperText>{primaryDateTypeError}</FormHelperText>
                          )}
                        </FormControl>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ width: "100%" }}>
                        <DatePicker
                          name="Secondary Date (Optional)"
                          value={secondaryDate}
                          error=""
                          onPick={handleSecondaryDateChange}
                          views={["year", "month", "day"]}
                          label="Secondary Date (Optional)"
                          dateFormat="MM/DD/YYYY"
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ width: "100%", mt: 5.8 }}>
                        <FormControl size="small" fullWidth>
                          <InputLabel id="secondary-date-type-label">
                            Secondary Date Type
                          </InputLabel>
                          <Select
                            labelId="secondary-date-type-label"
                            value={secondaryDateType}
                            label="Secondary Date Type"
                            onChange={(e) => {
                              setSecondaryDateType(e.target.value);
                            }}
                            variant="outlined"
                            disabled={!secondaryDate}
                          >
                            {[
                              { value: "OFF_SHELF_BAN_DATE", label: "Off-Shelf Ban Date" },
                              { value: "MA_DATE", label: "Market Available (MA) Date" },
                              { value: "POM_DATE", label: "Place On Market (POM) Date" },
                              { value: "LAST_PRODUCTION_DATE", label: "Last Production Date" },
                              { value: "LAST_IMPORTATION_DATE", label: "Last Importation Date" },
                              { value: "DISPOSAL_DATE", label: "Disposal Date" },
                              { value: "COMMENCEMENT_DATE", label: "Commencement Date" },
                            ]
                              .filter((opt) => opt.value !== primaryDateType)
                              .map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                  {opt.label}
                                </MenuItem>
                              ))}
                          </Select>
                        </FormControl>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ width: "100%" }}>
                        <DatePicker
                          name="UL Internal Date (Optional)"
                          value={ulInternalDate}
                          error=""
                          onPick={(date) => setUlInternalDate(date)}
                          views={["year", "month", "day"]}
                          label="UL Internal Date (Optional)"
                          dateFormat="MM/DD/YYYY"
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ width: "100%", mt: 5.8 }}>
                        <FormControl
                          fullWidth
                          size="small"
                          error={!!restrictionTypeError}
                        >
                          <InputLabel id="restriction-type-label">
                            Restriction Type *
                          </InputLabel>
                          <Select
                            labelId="restriction-type-label"
                            value={restrictionType}
                            label="Restriction Type *"
                            onChange={(e) => {
                              const newType = e.target.value;
                              setRestrictionType(newType);
                              if (newType === "BANNED") {
                                setSelectedCategories((prevCategories) =>
                                  prevCategories.map((category) =>
                                    category.restrictionDescriptionError
                                      ? {
                                        ...category,
                                        restrictionDescriptionError: false,
                                      }
                                      : category
                                  )
                                );
                              }
                              if (newType) {
                                setRestrictionTypeError("");
                              }
                            }}
                            required
                            variant="outlined"
                          >
                            <MenuItem value="BANNED">Banned</MenuItem>
                            <MenuItem value="RESTRICTED">Restricted</MenuItem>
                            <MenuItem value="LABELING_REQUIREMENTS">
                              Labeling Requirements
                            </MenuItem>
                            <MenuItem value="RESTRICTIONS_AND_LABELING">
                              Restrictions and Labeling
                            </MenuItem>
                            <MenuItem value="RESTRICTED_AND_SDS">
                              Restricted and SDS
                            </MenuItem>
                          </Select>
                          {restrictionTypeError && (
                            <FormHelperText>{restrictionTypeError}</FormHelperText>
                          )}
                        </FormControl>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Additional Comments"
                        multiline
                        minRows={2}
                        maxRows={4}
                        size="small"
                        fullWidth
                        placeholder="Enter any additional details about the restriction (optional)"
                        value={additionalComments}
                        onChange={(e) => setAdditionalComments(e.target.value)}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* Dropdown */}
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  variant="outlined"
                  className="formSection"
                >
                  <Typography className="sectionTitle">
                    Approval Managers
                  </Typography>
                  <FormControl
                    fullWidth
                    size="small"
                    error={!!approvalmanagersError}
                  >
                    <InputLabel id="approval-managers-label">
                      Approval Managers
                    </InputLabel>
                    <Select
                      value={selectedManagerId}
                      onChange={(e) => {
                        const managerId = e.target.value;
                        const selectedManager = approvalManagersOptions.find(
                          (option) => option.id === managerId
                        );

                        if (selectedManager) {
                          setApprovalManagersList((prevList) => [
                            ...prevList,
                            selectedManager,
                          ]);

                          setapprovalManagersOptions((prevOptions) =>
                            prevOptions.filter((option) => option.id !== managerId)
                          );

                          setSelectedManagerId("");
                          setApprovalmanagersError("");
                        }
                      }}
                      labelId="approval-managers-label"
                      label="Approval Managers"
                      required
                      variant="outlined"
                      fullWidth
                      size="small"
                      error={!!approvalmanagersError}
                    >
                      {approvalManagersOptions.map((option) => (
                        <MenuItem key={option.id} value={option.id}>
                          {option.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {approvalmanagersError && (
                      <FormHelperText error>
                        {approvalmanagersError}
                      </FormHelperText>
                    )}
                  </FormControl>
                  <Box className="chipList">
                    {approvalManagersList.map((value, idx) => (
                      <Chip
                        key={idx}
                        label={value.name}
                        size="small"
                        sx={{
                          fontWeight: 520,
                          "& .MuiChip-icon": {
                            color: red[600],
                          },
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          setApprovalManagersList(
                            approvalManagersList.filter((item) => item !== value)
                          );
                          setapprovalManagersOptions([
                            ...approvalManagersOptions,
                            value,
                          ]);
                        }}
                        icon={<CancelIcon />}
                      />
                    ))}
                  </Box>
                </Paper>
              </Grid>

              {/* R&D Coordinator Assignment — visible only for UI Exit Requests */}
              {isUiExitRequest && (
                <Grid item xs={12}>
                  <Paper
                    elevation={0}
                    variant="outlined"
                    className="formSection"
                    sx={{
                      borderColor: coordinatorError ? "error.main" : "primary.light",
                      borderWidth: coordinatorError ? 2 : 1,
                      transition: "border-color 0.2s",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                      <Typography className="sectionTitle" sx={{ mb: 0 }}>
                        R&amp;D Coordinator
                      </Typography>
                      <Chip
                        label="Required"
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ fontSize: "0.65rem", height: 18, mb: 1.2 }}
                      />
                    </Box>
                    {/* <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
                      Select exactly one R&amp;D Coordinator who will define the product list for all regions
                      before R&amp;D members can start their analysis.
                    </Typography> */}

                    <FormControl
                      fullWidth
                      size="small"
                      error={!!coordinatorError}
                      disabled={!!selectedCoordinatorDisplay}
                    >
                      <InputLabel id="coordinator-label">R&amp;D Coordinator *</InputLabel>
                      <Select
                        labelId="coordinator-label"
                        value={selectedCoordinatorId}
                        label="R&D Coordinator *"
                        onChange={(e) => {
                          const coordId = e.target.value;
                          const coord = coordinatorOptions.find((c) => c.id === coordId);
                          if (coord) {
                            setSelectedCoordinatorId(coordId);
                            setSelectedCoordinatorDisplay(coord);
                            setCoordinatorError("");
                          }
                        }}
                        variant="outlined"
                        required
                      >
                        {coordinatorOptions.length === 0 ? (
                          <MenuItem disabled value="">
                            No R&amp;D Coordinators available
                          </MenuItem>
                        ) : (
                          coordinatorOptions.map((option) => (
                            <MenuItem key={option.id} value={option.id}>
                              {option.name}
                            </MenuItem>
                          ))
                        )}
                      </Select>
                      {coordinatorError && (
                        <FormHelperText error>{coordinatorError}</FormHelperText>
                      )}
                    </FormControl>

                    {selectedCoordinatorDisplay && (
                      <Box className="chipList" sx={{ mt: 1 }}>
                        <Chip
                          label={selectedCoordinatorDisplay.name}
                          size="small"
                          color="primary"
                          sx={{
                            fontWeight: 520,
                            "& .MuiChip-icon": { color: red[600] },
                            cursor: "pointer",
                          }}
                          onClick={() => {
                            setSelectedCoordinatorId("");
                            setSelectedCoordinatorDisplay(null);
                          }}
                          icon={<CancelIcon />}
                        />
                      </Box>
                    )}
                  </Paper>
                </Grid>
              )}

              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  variant="outlined"
                  className="formSection categoriesSection"
                >
                  <Typography className="sectionTitle">
                    Categories
                  </Typography>

                  {!currentRegion && <Typography
                    variant="body1"
                    sx={{ mb: 1, color: "text.secondary" }}
                  >
                    Select a region to see available categories
                  </Typography>}
                  <CategoryList
                    noSelections={selectedCategories.length === 0}
                    items={[...selectedCategories]}
                    onDelete={handleDeleteItem}
                    onMemberSelection={handleSelectMember}
                    showDescriptions={!!requiresCategoryDescriptions || isEditMode}
                    requiresDescriptions={false}
                    onDescriptionChange={handleCategoryDescriptionChange}
                  />
                </Paper>
              </Grid>
            </Grid>
          </Box>
          <Box className="pageFooterActions">
            <Button
              className="submissionButton"
              variant="outlined"
              onClick={handleOnCancel}
              size="large"
              disabled={postLoading}
            >
              Cancel
            </Button>
            <Button
              className="submissionButton"
              type="button"
              variant="contained"
              size="large"
              onClick={handleSaveClick}
              disabled={postLoading || ingredientsLoading || isLoading}
            >
              {!postLoading ? (
                isEditMode ? (
                  "Update"
                ) : (
                  "Submit"
                )
              ) : (
                <LoadingComponent size="2em" thickness={5} color="inherit" />
              )}
            </Button>
          </Box>
        </Grid>
        <Grid item xs={12} md={3.5} className="regionsSideBar">
          <TabContext value="regionsPanel">
            <TabPanel
              value="regionsPanel"
              classes={{ root: "overrideTabPanelRoot" }}
            >
              <RegionsSideBar
                selectedCategories={selectedCategories}
                setSelectedCategories={setSelectedCategories}
                categories={categories}
                setCategories={setCategories}
                regions={regions}
                setRegions={setRegions}
                currentRegion={currentRegion}
                setCurrentRegion={setCurrentRegion}
                targetedMembers={targetedMem}
                selectedCountries={selectedCountries}
                setSelectedCountries={setSelectedCountries}
                countryError={countryError}
                setCountryError={setCountryError}
                isUiExitRequest={isUiExitRequest}
              />
            </TabPanel>
          </TabContext>
        </Grid>
      </Grid>
    </NewIngredientContainer>
  );
};

export default CreateIngredientExit;
